/**
 * Links into Bindles, the system of record for namespace access.
 *
 * Lives in `common/` rather than under the namespace feature because two
 * unrelated surfaces need the SAME href: the namespace page's metadata card, and
 * `common/authz/AccessDeniedAlert`, which offers a refused user the Bindle to
 * request access in. A second implementation would eventually send the user to a
 * Bindles environment where the resource does not exist.
 */

import { getStage, Stage } from 'src/components/common/util/StageHelpers';

/**
 * Bindles has two live environments. Non-prod LangHub stages register their
 * bindle resources in the gamma one, so linking a beta/gamma/test resource id to
 * prod Bindles would 404 — and vice versa.
 */
const BINDLES_PROD_ORIGIN = 'https://bindles.amazon.com';
const BINDLES_GAMMA_ORIGIN = 'https://bindles-gamma.amazon.com';

/**
 * Link to a bindle resource page in the Bindles environment matching the
 * running stage: prod → bindles.amazon.com, everything else →
 * bindles-gamma.amazon.com.
 *
 * Answers `undefined` for a missing id so callers can render nothing rather than
 * a link to a resource-less path.
 */
export function buildBindleHref(
  bindleResourceId?: string | null,
): string | undefined {
  if (!bindleResourceId) return undefined;
  const origin =
    getStage() === Stage.Prod ? BINDLES_PROD_ORIGIN : BINDLES_GAMMA_ORIGIN;
  return `${origin}/resource/${bindleResourceId}`;
}

/** The denied namespace paired with the Bindle page that grants access to it. */
export interface BindleAccessLink {
  readonly namespaceId: string;
  readonly href: string;
}

/**
 * The Bindle link actually offerable for a denied namespace, or `undefined` when
 * there is none.
 *
 * The single place that decides whether a refusal HAS a link. Two surfaces need
 * that answer before rendering — `AccessDeniedAlert` and the submit flash — to
 * choose between the with-link layout and the message-only one; deciding it once
 * keeps them from wrapping a message in a container that holds nothing.
 *
 * A namespace with no resolvable href answers `undefined`: a link to a
 * resource-less Bindle path is a 404 offered as the route to access, and no link
 * is the same treatment as the service omitting the detail.
 */
export function resolveBindleAccessLink(
  deniedNamespace?: {
    readonly namespaceId: string;
    readonly bindleResourceId: string;
  } | null,
): BindleAccessLink | undefined {
  if (!deniedNamespace) return undefined;
  const href = buildBindleHref(deniedNamespace.bindleResourceId);
  return href ? { namespaceId: deniedNamespace.namespaceId, href } : undefined;
}
